import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { withRouter } from "react-router-dom";
import { apiUrl } from "../../../config/config";
import SideBar from "../../common/SideBar";
import {
  subscribe as subscribeQueue,
  removeItem as removeQueueItem,
  retryItem as retryQueueItem,
  clearFinished as clearQueueFinished,
  CODE_LABELS as QUEUE_CODE_LABELS,
} from "../../verifier/analysisQueue";

/**
 * AnalysisDashboard
 * ──────────────────
 * Live operations view:
 *   • Top stats bar (running / queued / completed today / failed today)
 *   • Server pool grid (4 Pool A + 2 Pool B) — idle / busy / down
 *   • In-flight analyses table — phase chip, dataset detail, % progress
 *   • Recent completions
 *
 * Polls one endpoint every 5 sec:
 *   GET <apiUrl>api/users/getAnalysisDashboard
 *
 * Expected backend response shape — implement on the backend if not yet:
 *
 * {
 *   "stats": {
 *      "running":         3,
 *      "queued":          1,
 *      "completed_today": 12,
 *      "failed_today":    1
 *   },
 *   "servers": [
 *      {
 *         "id":          "vm-a1",                 // canonical
 *         "pool":        "A",
 *         "status":      "busy",                  // idle / busy / down
 *         "current_job": {                        // only if busy
 *            "userid":   "<id>",
 *            "patname":  "John Doe",
 *            "pipeline": "first-code",
 *            "phase":    "running",
 *            "phase_detail": "dataset 12 (4/25)",
 *            "progress": 42,
 *            "started_at": "2026-04-25T10:00:00+05:30"
 *         }
 *      },
 *      ...  // 6 entries: vm-a1, vm-a2, vm-a3, vm-a4-azure, vm-b1, vm-b2
 *   ],
 *   "running": [                                  // duplicates current_job for table view
 *      { "userid":"...", "patname":"...", "pipeline":"first-code",
 *        "node_host":"vm-a1", "phase":"running", "phase_detail":"...",
 *        "progress": 42, "started_at":"...", "heartbeat":"..." }
 *   ],
 *   "queued": [
 *      { "userid":"...", "patname":"...", "pipeline":"second-code",
 *        "preferred_server":"vm-a2", "queued_at":"..." }
 *   ],
 *   "recent": [
 *      { "userid":"...", "patname":"...", "pipeline":"first-code",
 *        "node_host":"vm-a1", "phase":"complete",
 *        "started_at":"...", "finished_at":"...", "duration_seconds": 412 }
 *   ]
 * }
 */

// ── Naming maps (mirror frontend AddModal.js & backend canonical IDs) ──
const SERVER_LABEL = {
  "vm-a1":        "VM-A1 · On-Premises · IP:11",
  "vm-a2":        "VM-A2 · On-Premises · IP:14",
  "vm-a3":        "VM-A3 · On-Premises · IP:111",
  "vm-a4-azure":  "VM-A4 · Azure · IP:77",
  "vm-b1":        "VM-B1 · On-Premises · IP:12",
  "vm-b2":        "VM-B2 · On-Premises · IP:119",
};

const PIPELINE_LABEL = {
  "first-code":  "Full AI",
  "second-code": "Hybrid",
  "third-code":  "Classical",
  "fourth-code": "NegPeak",
  "analytics2":  "Rapid AI",
  "inversion":   "Invert",
  "datacopy":    "Data Sync",
};

const PIPELINE_COLOR = {
  "first-code":  "#2D7EF7",
  "second-code": "#3F8E48",
  "third-code":  "#E96D2D",
  "fourth-code": "#7E3F98",
  "analytics2":  "#1FA9A0",
  "inversion":   "#2D7EF7",
  "datacopy":    "#3F8E48",
};

const PHASE_CONFIG = {
  queued:          { color: "#94a3b8", label: "QUEUED" },
  ssh_connecting:  { color: "#38bdf8", label: "CONNECTING" },
  ssh_connected:   { color: "#38bdf8", label: "CONNECTED" },
  running:         { color: "#10b981", label: "RUNNING" },
  stuck:           { color: "#f59e0b", label: "STUCK" },
  failed:          { color: "#ef4444", label: "FAILED" },
  complete:        { color: "#10b981", label: "COMPLETE" },
};

const STATUS_CONFIG = {
  idle: { color: "#10b981", label: "IDLE",  glow: "#10b98144" },
  busy: { color: "#f59e0b", label: "BUSY",  glow: "#f59e0b44" },
  down: { color: "#ef4444", label: "DOWN",  glow: "#ef444444" },
};

// Browser-queue item statuses (see analysisQueue.js) → chip config
const QUEUE_STATUS_CONFIG = {
  waiting:   { color: "#94a3b8", label: "WAITING" },
  resolving: { color: "#38bdf8", label: "LOADING" },
  starting:  { color: "#38bdf8", label: "STARTING" },
  retrying:  { color: "#f59e0b", label: "RETRYING" },
  running:   { color: "#10b981", label: "RUNNING" },
  done:      { color: "#10b981", label: "DONE" },
  failed:    { color: "#ef4444", label: "FAILED" },
};

const FONT = '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif';
const REFRESH_MS = 5000;

// Pool layout — fixed order matters for visual grouping
const POOL_A = ["vm-a1", "vm-a2", "vm-a3", "vm-a4-azure"];
const POOL_B = ["vm-b1", "vm-b2"];

// Empty-state fallback so the dashboard renders even if the endpoint isn't ready
const EMPTY_DATA = {
  stats: { running: 0, queued: 0, completed_today: 0, failed_today: 0 },
  servers: [...POOL_A, ...POOL_B].map((id) => ({
    id,
    pool: id.startsWith("vm-a") ? "A" : "B",
    status: "down",
    current_job: null,
  })),
  running: [],
  queued: [],
  recent: [],
};

function formatDuration(seconds) {
  if (!seconds || seconds < 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

function formatRelative(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleString();
}

function AnalysisDashboard(props) {
  // Admin-only route. SideBar already hides the menu for non-admins;
  // this is defense-in-depth in case anyone types the URL directly.
  // NOTE: hooks must be called in the same order on every render, so
  // the redirect goes through useEffect (not an early return) and the
  // body just renders `null` while the navigation kicks in.
  const isAdminVerifier =
    localStorage.getItem("verifiertype") === "8";

  const [data, setData] = useState(EMPTY_DATA);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [endpointMissing, setEndpointMissing] = useState(false);
  // Sequential analysis queue — lives in the Node backend (analysis_queue
  // collection + worker); this just polls the list. subscribe() returns
  // its unsubscribe for cleanup and reports fetch errors so we can show
  // an honest banner when the backend routes are missing.
  const [queueItems, setQueueItems] = useState([]);
  const [queueError, setQueueError] = useState(null);
  useEffect(
    () =>
      subscribeQueue((items, err) => {
        setQueueItems(items);
        setQueueError(err || null);
      }),
    []
  );

  useEffect(() => {
    if (!isAdminVerifier) {
      if (props.history && props.history.replace) {
        props.history.replace("/verifier-home");
      } else {
        window.location.href = "/verifier-home";
      }
    }
    // intentional: only fires on mount + on isAdminVerifier change
    // (won't change mid-session, but keeps the lint check happy).
  }, [isAdminVerifier, props.history]);

  useEffect(() => {
    let mounted = true;
    const fetchOnce = async () => {
      try {
        const res = await axios.get(`${apiUrl}api/users/getAnalysisDashboard`);
        if (!mounted) return;
        setData({ ...EMPTY_DATA, ...(res.data || {}) });
        setError(null);
        setEndpointMissing(false);
        setLastUpdated(new Date());
      } catch (err) {
        if (!mounted) return;
        const status = err && err.response && err.response.status;
        if (status === 404) {
          setEndpointMissing(true);
        } else {
          setError(err && err.message ? err.message : "fetch failed");
        }
      }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, REFRESH_MS);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  const serversByPool = useMemo(() => {
    const map = {};
    (data.servers || []).forEach((s) => { map[s.id] = s; });
    return {
      A: POOL_A.map((id) => map[id] || { id, pool: "A", status: "down", current_job: null }),
      B: POOL_B.map((id) => map[id] || { id, pool: "B", status: "down", current_job: null }),
    };
  }, [data.servers]);

  // ── Styles ───────────────────────────────────────────────────────────
  const pageWrap = {
    fontFamily: FONT,
    background: "#0b1220",
    minHeight: "100vh",
    padding: "24px 28px",
    color: "#e2e8f0",
  };
  const headerRow = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "20px",
  };
  const h1 = {
    margin: 0,
    fontSize: "22px",
    fontWeight: 800,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#f8fafc",
  };
  const lastUpdatedLine = {
    fontSize: "12px",
    color: "#94a3b8",
  };
  const sectionTitle = {
    margin: "28px 0 14px",
    fontSize: "12px",
    fontWeight: 800,
    letterSpacing: "0.22em",
    textTransform: "uppercase",
    color: "#cbd5e1",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  };
  const sectionLine = {
    flex: 1,
    height: "1px",
    background: "linear-gradient(90deg, rgba(148,163,184,0.3), rgba(148,163,184,0))",
  };
  const cardBase = {
    background: "linear-gradient(160deg, rgba(30,41,59,0.95) 0%, rgba(15,23,42,0.95) 100%)",
    borderRadius: "14px",
    padding: "16px 18px",
    boxShadow: "0 0 0 1px rgba(148,163,184,0.15)",
  };
  const statsGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "14px",
  };
  const statCard = (accent) => ({
    ...cardBase,
    borderTop: `3px solid ${accent}`,
  });
  const statLabel = {
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: "#94a3b8",
  };
  const statValue = {
    fontSize: "32px",
    fontWeight: 800,
    color: "#f8fafc",
    marginTop: "6px",
    lineHeight: 1,
  };

  const serverGrid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "14px",
  };
  const serverCard = (statusColor) => ({
    ...cardBase,
    position: "relative",
    overflow: "hidden",
    borderLeft: `3px solid ${statusColor}`,
  });

  const tableWrap = {
    ...cardBase,
    padding: "14px",
  };
  const tableHead = {
    display: "grid",
    gridTemplateColumns: "180px 1fr 140px 140px 1fr 90px 100px",
    gap: "12px",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#94a3b8",
    padding: "10px 8px",
    borderBottom: "1px solid rgba(148,163,184,0.2)",
  };
  const tableRow = {
    display: "grid",
    gridTemplateColumns: "180px 1fr 140px 140px 1fr 90px 100px",
    gap: "12px",
    fontSize: "13px",
    color: "#e2e8f0",
    padding: "12px 8px",
    borderBottom: "1px solid rgba(148,163,184,0.08)",
    alignItems: "center",
  };
  const recentRow = {
    ...tableRow,
    gridTemplateColumns: "180px 1fr 140px 140px 100px 100px",
  };
  const recentHead = {
    ...tableHead,
    gridTemplateColumns: "180px 1fr 140px 140px 100px 100px",
  };

  const progressBarOuter = {
    background: "rgba(15,23,42,0.7)",
    borderRadius: "999px",
    height: "8px",
    overflow: "hidden",
    border: "1px solid rgba(148,163,184,0.2)",
  };
  const phaseChip = (color) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 10px",
    borderRadius: "999px",
    border: `1px solid ${color}55`,
    background: `${color}1f`,
    color,
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.14em",
  });
  const pipelinePill = (color) => ({
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: "8px",
    background: `${color}22`,
    color,
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.06em",
  });

  // ── Render helpers ───────────────────────────────────────────────────
  const renderPhaseChip = (phase) => {
    const cfg = PHASE_CONFIG[phase] || { color: "#94a3b8", label: (phase || "?").toUpperCase() };
    return (
      <span style={phaseChip(cfg.color)}>
        <span style={{
          display: "inline-block",
          width: "5px",
          height: "5px",
          borderRadius: "50%",
          background: cfg.color,
          boxShadow: `0 0 6px ${cfg.color}`,
        }}></span>
        {cfg.label}
      </span>
    );
  };

  const renderPipelinePill = (pipeline) => {
    const color = PIPELINE_COLOR[pipeline] || "#64748b";
    const label = PIPELINE_LABEL[pipeline] || pipeline || "—";
    return <span style={pipelinePill(color)}>{label}</span>;
  };

  const renderProgress = (pct) => {
    const v = Math.max(0, Math.min(100, pct || 0));
    return (
      <div>
        <div style={progressBarOuter}>
          <div style={{
            width: `${v}%`,
            height: "100%",
            background: "linear-gradient(90deg, #10b981, #38bdf8)",
            transition: "width 400ms ease",
          }}></div>
        </div>
        <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px" }}>{v}%</div>
      </div>
    );
  };

  // Inline meter — small horizontal bar with label / value / colored fill.
  // Color goes green→amber→red as the value climbs (configurable thresholds).
  const renderMeter = (label, value, opts = {}) => {
    if (value == null) return null;
    const { warn = 60, crit = 85, suffix = "%", width = "100%" } = opts;
    const v = Math.max(0, Math.min(100, value));
    const color = v >= crit ? "#ef4444" : v >= warn ? "#f59e0b" : "#10b981";
    return (
      <div style={{ width, marginTop: "4px" }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "10px",
          fontWeight: 600,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "#94a3b8",
          marginBottom: "2px",
        }}>
          <span>{label}</span>
          <span style={{ color, fontVariantNumeric: "tabular-nums" }}>{v.toFixed(0)}{suffix}</span>
        </div>
        <div style={{
          height: "4px",
          borderRadius: "999px",
          background: "rgba(15,23,42,0.7)",
          border: "1px solid rgba(148,163,184,0.18)",
          overflow: "hidden",
        }}>
          <div style={{
            width: `${v}%`,
            height: "100%",
            background: color,
            transition: "width 400ms ease, background 400ms ease",
          }}></div>
        </div>
      </div>
    );
  };

  const renderServerCard = (server) => {
    const statusCfg = STATUS_CONFIG[server.status] || STATUS_CONFIG.down;
    const job = server.current_job;
    const heartbeatAge = server.last_seen
      ? Math.floor((Date.now() - new Date(server.last_seen).getTime()) / 1000)
      : null;
    return (
      <div key={server.id} style={serverCard(statusCfg.color)}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: "13px", fontWeight: 700, color: "#f8fafc" }}>
              {SERVER_LABEL[server.id] || server.id}
            </div>
            <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "2px" }}>
              Pool {server.pool}
              {heartbeatAge != null && (
                <span style={{
                  marginLeft: "8px",
                  color: heartbeatAge > 90 ? "#ef4444" : "#64748b",
                }}>
                  · ♥ {heartbeatAge}s ago
                </span>
              )}
            </div>
          </div>
          <span style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "3px 10px",
            borderRadius: "999px",
            background: `${statusCfg.color}22`,
            color: statusCfg.color,
            fontSize: "10px",
            fontWeight: 700,
            letterSpacing: "0.14em",
          }}>
            <span style={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              background: statusCfg.color,
              boxShadow: `0 0 8px ${statusCfg.color}`,
            }}></span>
            {statusCfg.label}
          </span>
        </div>

        {/* Live VM metrics from heartbeat — only render if data is present */}
        {(server.load_pct_1m != null || server.mem_pct != null || server.disk_pct != null) && (
          <div style={{
            marginTop: "12px",
            padding: "10px 12px",
            background: "rgba(15,23,42,0.45)",
            borderRadius: "10px",
            border: "1px solid rgba(148,163,184,0.12)",
          }}>
            {renderMeter("CPU Load (1m)", server.load_pct_1m, { warn: 60, crit: 85 })}
            {renderMeter("Memory",         server.mem_pct,     { warn: 75, crit: 90 })}
            {renderMeter("Disk",           server.disk_pct,    { warn: 80, crit: 92 })}
            {(server.cpu_pct != null || server.running_pys != null) && (
              <div style={{
                marginTop: "8px",
                paddingTop: "6px",
                borderTop: "1px dashed rgba(148,163,184,0.18)",
                display: "flex",
                gap: "12px",
                fontSize: "10px",
                color: "#94a3b8",
                letterSpacing: "0.04em",
              }}>
                {server.cpu_pct != null && (
                  <span>CPU now: <span style={{ color: "#cbd5e1", fontVariantNumeric: "tabular-nums" }}>{server.cpu_pct.toFixed(0)}%</span></span>
                )}
                {server.running_pys != null && (
                  <span>Pythons: <span style={{ color: "#cbd5e1" }}>{server.running_pys}</span></span>
                )}
              </div>
            )}
          </div>
        )}

        {job ? (
          <div style={{ marginTop: "14px" }}>
            <div style={{ marginBottom: "8px" }}>
              {renderPipelinePill(job.pipeline)}
            </div>
            <div style={{ fontSize: "13px", color: "#f1f5f9", marginBottom: "4px" }}>
              {job.patname || job.userid}
            </div>
            <div style={{ fontSize: "11px", color: "#94a3b8", marginBottom: "8px", minHeight: "14px" }}>
              {job.phase_detail || ""}
            </div>
            {renderProgress(job.progress)}
          </div>
        ) : (
          <div style={{ marginTop: "14px", fontSize: "12px", color: "#64748b", fontStyle: "italic" }}>
            {server.status === "down" ? "Server offline" : "Waiting for work"}
          </div>
        )}
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────
  const goBack = () => {
    if (props && props.history && typeof props.history.push === "function") {
      props.history.push("/patient-list");
    } else {
      window.location.href = "/patient-list";
    }
  };

  // Short-circuit the render for non-admins. The useEffect above is
  // already redirecting them away; this prevents a flash of the
  // dashboard between mount and navigation.
  if (!isAdminVerifier) {
    return null;
  }

  return (
    <div style={pageWrap}>
      <SideBar
        menus={[
          { title: "Patient List",       url: "/patient-list" },
          { title: "Verifier Home",      url: "/verifier-home" },
          { title: "Analysis Dashboard", url: "/analysis-dashboard" },
          { title: "Users by Role",      url: "/users-by-role" },
          { title: "Telemetry",          url: "/telemetry" },
          { title: "Assign Patients",    url: "/assign-patients" },
          { title: "Verified",           url: "/Verification-Completed" },
          { title: "QC",                 url: "/qc_home" },
        ]}
        profileLink="verifier-profile"
      />
      <div style={headerRow}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <button
            onClick={goBack}
            style={{
              background: "rgba(148,163,184,0.18)",
              color: "#e2e8f0",
              border: "none",
              borderRadius: "8px",
              padding: "8px 14px",
              fontSize: "12px",
              fontWeight: 600,
              letterSpacing: "0.04em",
              cursor: "pointer",
              fontFamily: FONT,
            }}
          >
            ← Back
          </button>
          <h1 style={h1}>Analysis Dashboard</h1>
        </div>
        <div style={lastUpdatedLine}>
          {lastUpdated
            ? `Last updated ${formatRelative(lastUpdated.toISOString())}  ·  refreshes every ${REFRESH_MS / 1000}s`
            : "Loading…"}
        </div>
      </div>

        {endpointMissing && (
          <div style={{
            ...cardBase,
            borderLeft: "3px solid #f59e0b",
            marginBottom: "16px",
            color: "#fcd34d",
            fontSize: "13px",
          }}>
            <strong>Backend endpoint not yet implemented.</strong>
            <span style={{ color: "#cbd5e1" }}>
              {" "}This page expects <code style={{ background: "rgba(0,0,0,0.3)", padding: "2px 6px", borderRadius: "4px" }}>
                GET {apiUrl}api/users/getAnalysisDashboard
              </code>. The contract is documented in the file's top JSDoc.
              Once it's implemented, this banner disappears and live data renders here.
            </span>
          </div>
        )}

        {error && !endpointMissing && (
          <div style={{
            ...cardBase,
            borderLeft: "3px solid #ef4444",
            marginBottom: "16px",
            color: "#fca5a5",
            fontSize: "13px",
          }}>
            Failed to load dashboard data: {error}
          </div>
        )}

        {/* Stats */}
        <div style={statsGrid}>
          <div style={statCard("#10b981")}>
            <div style={statLabel}>Running</div>
            <div style={statValue}>{data.stats.running || 0}</div>
          </div>
          <div style={statCard("#38bdf8")}>
            <div style={statLabel}>Queued</div>
            <div style={statValue}>{data.stats.queued || 0}</div>
          </div>
          <div style={statCard("#10b981")}>
            <div style={statLabel}>Completed Today</div>
            <div style={statValue}>{data.stats.completed_today || 0}</div>
          </div>
          <div style={statCard("#ef4444")}>
            <div style={statLabel}>Failed Today</div>
            <div style={statValue}>{data.stats.failed_today || 0}</div>
          </div>
        </div>

        {/* Sequential analysis queue — filled by the "Queue" button in the
            analyze window; runs strictly one analysis at a time. */}
        <div style={sectionTitle}>
          <span style={{
            display: "inline-block",
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            background: "#38bdf8",
            boxShadow: "0 0 10px #38bdf8",
          }}></span>
          Analysis Queue ({queueItems.length})
          <span style={sectionLine}></span>
          {queueItems.some((q) => ["failed", "done"].includes(q.status)) && (
            <button
              onClick={clearQueueFinished}
              style={{
                background: "rgba(148,163,184,0.18)",
                color: "#cbd5e1",
                border: "none",
                borderRadius: "6px",
                padding: "4px 10px",
                fontSize: "11px",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: FONT,
                letterSpacing: "0.04em",
                textTransform: "none",
              }}
            >
              Clear finished
            </button>
          )}
        </div>
        {queueError && (
          <div style={{
            ...cardBase,
            borderLeft: "3px solid #f59e0b",
            marginBottom: "12px",
            color: "#fcd34d",
            fontSize: "13px",
          }}>
            <strong>Queue backend not reachable:</strong>
            <span style={{ color: "#cbd5e1" }}> {queueError}. This page expects the{" "}
              <code style={{ background: "rgba(0,0,0,0.3)", padding: "2px 6px", borderRadius: "4px" }}>
                analysisQueue
              </code>{" "}routes + worker in this web server's backend (add/list/retry/clearFinished).
            </span>
          </div>
        )}
        <div style={tableWrap}>
          {queueItems.length === 0 ? (
            <div style={{ padding: "14px 8px", fontSize: "13px", color: "#64748b", fontStyle: "italic" }}>
              Queue is empty. Open a patient's analyze window, pick the code, server and
              start/end time, then press <strong style={{ color: "#94a3b8" }}>Queue</strong> —
              the backend runs items one at a time.
            </div>
          ) : (
            <>
              <div style={{ ...tableHead, gridTemplateColumns: "1fr 120px 110px 120px 1fr 110px" }}>
                <div>Patient</div>
                <div>Pipeline</div>
                <div>Server</div>
                <div>Status</div>
                <div>Progress</div>
                <div></div>
              </div>
              {queueItems.map((it) => {
                const st = QUEUE_STATUS_CONFIG[it.status] || QUEUE_STATUS_CONFIG.waiting;
                const pillColor = PIPELINE_COLOR[it.codeType] || "#64748b";
                const active = ["resolving", "starting", "retrying", "running"].includes(it.status);
                return (
                  <div key={it.qid} style={{ ...tableRow, gridTemplateColumns: "1fr 120px 110px 120px 1fr 110px" }}>
                    <div>
                      <div style={{ fontWeight: 600, color: "#f1f5f9" }}>{it.name}</div>
                      {it.status === "failed" && it.error && (
                        <div style={{ fontSize: "11px", color: "#fca5a5", marginTop: "2px" }}>{it.error}</div>
                      )}
                    </div>
                    <div>
                      <span style={pipelinePill(pillColor)}>
                        {QUEUE_CODE_LABELS[it.codeType] || it.codeType}
                      </span>
                    </div>
                    <div style={{ fontSize: "12px", color: "#cbd5e1" }}>{it.server}</div>
                    <div>
                      <span style={phaseChip(st.color)}>
                        <span style={{
                          display: "inline-block",
                          width: "5px",
                          height: "5px",
                          borderRadius: "50%",
                          background: st.color,
                          boxShadow: `0 0 6px ${st.color}`,
                        }}></span>
                        {st.label}
                      </span>
                    </div>
                    <div>{active || it.status === "done" ? renderProgress(it.progress) : <span style={{ fontSize: "12px", color: "#64748b" }}>—</span>}</div>
                    <div style={{ display: "flex", gap: "6px", justifyContent: "flex-end" }}>
                      {it.status === "failed" && (
                        <button
                          onClick={() => retryQueueItem(it.qid)}
                          style={{
                            background: "rgba(16,185,129,0.15)",
                            color: "#10b981",
                            border: "1px solid rgba(16,185,129,0.5)",
                            borderRadius: "6px",
                            padding: "3px 10px",
                            fontSize: "11px",
                            fontWeight: 600,
                            cursor: "pointer",
                            fontFamily: FONT,
                          }}
                        >
                          Retry
                        </button>
                      )}
                      {it.status !== "done" && (
                        <button
                          onClick={() => removeQueueItem(it.qid)}
                          title="Remove from queue"
                          style={{
                            background: "none",
                            color: "#94a3b8",
                            border: "1px solid rgba(148,163,184,0.35)",
                            borderRadius: "6px",
                            padding: "3px 9px",
                            fontSize: "11px",
                            cursor: "pointer",
                            fontFamily: FONT,
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Pool A — Analysis */}
        <div style={sectionTitle}>
          <span style={{
            display: "inline-block",
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            background: "#10b981",
            boxShadow: "0 0 10px #10b981",
          }}></span>
          Pool A — Analysis Pipelines
          <span style={sectionLine}></span>
        </div>
        <div style={serverGrid}>
          {serversByPool.A.map(renderServerCard)}
        </div>

        {/* Pool B — Rapid AI */}
        <div style={sectionTitle}>
          <span style={{
            display: "inline-block",
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            background: "#1FA9A0",
            boxShadow: "0 0 10px #1FA9A0",
          }}></span>
          Pool B — Rapid AI
          <span style={sectionLine}></span>
        </div>
        <div style={serverGrid}>
          {serversByPool.B.map(renderServerCard)}
        </div>

        {/* In-flight analyses table */}
        <div style={sectionTitle}>
          <span style={{
            display: "inline-block",
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            background: "#10b981",
            boxShadow: "0 0 10px #10b981",
          }}></span>
          In-Flight Analyses ({(data.running || []).length})
          <span style={sectionLine}></span>
        </div>
        <div style={tableWrap}>
          <div style={tableHead}>
            <div>Patient</div>
            <div>Pipeline</div>
            <div>Server</div>
            <div>Phase</div>
            <div>Detail</div>
            <div style={{ textAlign: "right" }}>Started</div>
            <div>Progress</div>
          </div>
          {(data.running || []).length === 0 ? (
            <div style={{
              padding: "32px 8px",
              textAlign: "center",
              color: "#64748b",
              fontStyle: "italic",
              fontSize: "13px",
            }}>
              No analyses currently running
            </div>
          ) : (
            (data.running || []).map((job, idx) => (
              <div key={`${job.userid}-${idx}`} style={tableRow}>
                <div style={{ fontWeight: 600, color: "#f1f5f9" }}>
                  {job.patname || job.userid}
                </div>
                <div>{renderPipelinePill(job.pipeline)}</div>
                <div style={{ fontSize: "11px", color: "#cbd5e1" }}>
                  {SERVER_LABEL[job.node_host] || job.node_host || "—"}
                </div>
                <div>{renderPhaseChip(job.phase)}</div>
                <div style={{ fontSize: "12px", color: "#94a3b8" }}>
                  {job.phase_detail || "—"}
                </div>
                <div style={{ fontSize: "11px", color: "#94a3b8", textAlign: "right" }}>
                  {formatRelative(job.started_at)}
                </div>
                <div>{renderProgress(job.progress)}</div>
              </div>
            ))
          )}
        </div>

        {/* Queue */}
        {(data.queued || []).length > 0 && (
          <>
            <div style={sectionTitle}>
              <span style={{
                display: "inline-block",
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                background: "#38bdf8",
                boxShadow: "0 0 10px #38bdf8",
              }}></span>
              Queue ({(data.queued || []).length} waiting)
              <span style={sectionLine}></span>
            </div>
            <div style={tableWrap}>
              {(data.queued || []).map((job, idx) => (
                <div key={`${job.userid}-${idx}`} style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 8px",
                  borderBottom: "1px solid rgba(148,163,184,0.08)",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <span style={{
                      fontSize: "11px",
                      color: "#64748b",
                      width: "24px",
                    }}>#{idx + 1}</span>
                    <span style={{ color: "#f1f5f9", fontSize: "13px", fontWeight: 600 }}>
                      {job.patname || job.userid}
                    </span>
                    {renderPipelinePill(job.pipeline)}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    {job.preferred_server && (
                      <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                        prefers {SERVER_LABEL[job.preferred_server] || job.preferred_server}
                      </span>
                    )}
                    <span style={{ fontSize: "11px", color: "#64748b" }}>
                      {formatRelative(job.queued_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Recent completions */}
        <div style={sectionTitle}>
          <span style={{
            display: "inline-block",
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            background: "#cbd5e1",
            boxShadow: "0 0 10px #cbd5e1",
          }}></span>
          Recent Completions
          <span style={sectionLine}></span>
        </div>
        <div style={tableWrap}>
          <div style={recentHead}>
            <div>Patient</div>
            <div>Pipeline</div>
            <div>Server</div>
            <div>Outcome</div>
            <div>Duration</div>
            <div style={{ textAlign: "right" }}>Finished</div>
          </div>
          {(data.recent || []).length === 0 ? (
            <div style={{
              padding: "32px 8px",
              textAlign: "center",
              color: "#64748b",
              fontStyle: "italic",
              fontSize: "13px",
            }}>
              No recent completions
            </div>
          ) : (
            (data.recent || []).map((job, idx) => (
              <div key={`${job.userid}-${idx}`} style={recentRow}>
                <div style={{ fontWeight: 600, color: "#f1f5f9" }}>
                  {job.patname || job.userid}
                </div>
                <div>{renderPipelinePill(job.pipeline)}</div>
                <div style={{ fontSize: "11px", color: "#cbd5e1" }}>
                  {SERVER_LABEL[job.node_host] || job.node_host || "—"}
                </div>
                <div>{renderPhaseChip(job.phase)}</div>
                <div style={{ fontSize: "12px", color: "#cbd5e1" }}>
                  {formatDuration(job.duration_seconds)}
                </div>
                <div style={{ fontSize: "11px", color: "#94a3b8", textAlign: "right" }}>
                  {formatRelative(job.finished_at)}
                </div>
              </div>
            ))
          )}
        </div>
    </div>
  );
}

export default withRouter(AnalysisDashboard);
